import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Circle,
  Flex,
  Heading,
  HStack,
  Progress,
  ScaleFade,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";

function deriveProgress(apiResponse) {
  if (!apiResponse) return 0;
  const { current_rows = 0, error_rows = 0, total_rows = 0 } = apiResponse;
  if (total_rows <= 0) return 0;
  const pct = Math.floor(((current_rows + error_rows) / total_rows) * 100);
  return Math.min(pct, 98);
}

const STAGES = [
  { label: "Validating records", min: 0, max: 39 },
  { label: "Mapping database fields", min: 40, max: 79 },
  { label: "Saving to cloud", min: 80, max: 99 },
];

const POLL_INTERVAL_MS = 3000;

export default function StepProcessing({
  importId,
  getRosterImportResults,
  onComplete,
  onError,
  onBack,
  setStep,
  updateHistoryItem,
}) {
  const [progress, setProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState(0);
  const [statusLabel, setStatusLabel] = useState("Starting…");
  const [error, setError] = useState(null);
  const [isDone, setIsDone] = useState(false);
  const [hasErrorRows, setHasErrorRows] = useState(false);
  const [errorRowCount, setErrorRowCount] = useState(0);
  const [totalRowCount, setTotalRowCount] = useState(0);

  const isMountedRef = useRef(true);
  const intervalRef = useRef(null);
  const prevProgressRef = useRef(0);
  const completedRef = useRef(false);
  const hasErrorRowsRef = useRef(false);

  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const onBackRef = useRef(onBack);
  const setStepRef = useRef(setStep);
  const updateHistoryItemRef = useRef(updateHistoryItem);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);
  useEffect(() => {
    setStepRef.current = setStep;
  }, [setStep]);
  useEffect(() => {
    updateHistoryItemRef.current = updateHistoryItem;
  }, [updateHistoryItem]);

  useEffect(() => {
    if (!importId || !getRosterImportResults) return;

    completedRef.current = false;
    hasErrorRowsRef.current = false;
    prevProgressRef.current = 0;
    isMountedRef.current = true;

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const poll = async () => {
      if (completedRef.current || !isMountedRef.current) return;

      try {
        const res = await getRosterImportResults(importId);

        if (!isMountedRef.current) return;
        if (!res) return;

        const runtime = res.orchestration_status?.runtimeStatus;
        const outerStatus = res.status;
        const currentRows = res.current_rows ?? 0;
        const errorRows = res.error_rows ?? 0;
        const totalRows = res.total_rows ?? 0;
        const processedRows = currentRows + errorRows;

        updateHistoryItemRef.current?.(importId, {
          status: res.status,
          orchestration_status: res.orchestration_status,
          current_rows: currentRows,
          error_rows: errorRows,
          total_rows: totalRows,
        });

        if (outerStatus === "failed" || runtime === "Failed") {
          stopPolling();
          const msg =
            res.orchestration_status?.output ||
            "Import failed. Please try again.";
          setError(typeof msg === "string" ? msg : JSON.stringify(msg));
          setStatusLabel("Import failed");
          onErrorRef.current?.(msg);
          return;
        }

        const next = deriveProgress(res);
        const smoothed = Math.max(prevProgressRef.current, next);
        prevProgressRef.current = smoothed;
        setProgress(smoothed);

        const activeStage = STAGES.reduce(
          (acc, _stage, idx) => (smoothed >= STAGES[idx].min ? idx : acc),
          0,
        );
        setCurrentStage(activeStage);

        if (runtime === "Pending") {
          setStatusLabel("Queued – waiting for a worker…");
        } else if (totalRows > 0) {
          setStatusLabel(`Processing ${processedRows} of ${totalRows} rows…`);
        } else {
          setStatusLabel("Processing records…");
        }

        // Keep polling until every row is accounted for:
        // current_rows + error_rows must equal total_rows.
        // status, orchestration_status, completed_at — all ignored.
        const allAccountedFor =
          totalRows > 0 && currentRows + errorRows >= totalRows;

        if (allAccountedFor) {
          completedRef.current = true;
          stopPolling();

          updateHistoryItemRef.current?.(importId, {
            status: "completed",
            current_rows: currentRows,
            error_rows: errorRows,
            total_rows: totalRows,
          });

          prevProgressRef.current = 100;
          setProgress(100);
          setCurrentStage(STAGES.length - 1);
          setIsDone(true);
          setTotalRowCount(totalRows);
          setErrorRowCount(errorRows);

          if (errorRows > 0) {
            hasErrorRowsRef.current = true;
            setHasErrorRows(true);
            setStatusLabel(
              `Completed with ${errorRows} error${errorRows === 1 ? "" : "s"}`,
            );
          } else {
            setStatusLabel("Import complete!");
            setTimeout(() => {
              if (!isMountedRef.current) return;
              setStepRef.current("DONE");
              onCompleteRef.current?.();
            }, 2000);
          }
        }
      } catch (err) {
        stopPolling();
        const msg = err?.message || "Unexpected error during import.";
        setError(msg);
        setStatusLabel("Import failed");
        onErrorRef.current?.(msg);
      }
    };

    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      stopPolling();
      isMountedRef.current = false;
    };
  }, [importId, getRosterImportResults]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleContinue = () => {
    setStepRef.current("DONE");
    onCompleteRef.current?.();
  };

  const handleBack = () => {
    onBackRef.current?.();
  };

  const progressColor = error
    ? "red"
    : hasErrorRows
      ? "orange"
      : isDone
        ? "green"
        : "blue";

  const stageBg = useColorModeValue("gray.50", "gray.700");
  const stageBorderColor = useColorModeValue("gray.100", "gray.600");
  const headingColor = useColorModeValue("gray.800", "white");
  const subtitleColor = useColorModeValue("gray.500", "gray.400");

  return (
    <ScaleFade initialScale={0.9} in={true}>
      <VStack spacing={8} py={8} textAlign="center">
        <Circle
          size="80px"
          bg={useColorModeValue(
            error ? "red.50" : hasErrorRows ? "orange.50" : "blue.50",
            error ? "red.900" : hasErrorRows ? "orange.900" : "blue.900",
          )}
          color={error ? "red.500" : hasErrorRows ? "orange.500" : "blue.500"}
          position="relative"
        >
          <Box
            as="span"
            fontSize="3xl"
            animation={!isDone && !error ? "spin 2s linear infinite" : "none"}
            style={{ display: "inline-block" }}
          >
            {error ? "❌" : hasErrorRows ? "⚠️" : isDone ? "✅" : "⚙️"}
          </Box>
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to   { transform: rotate(360deg); }
            }
          `}</style>
        </Circle>

        <Box w="full">
          <Heading size="md" color={headingColor} mb={1}>
            {error
              ? "Import Failed"
              : hasErrorRows
                ? "Import Completed with Warnings"
                : isDone
                  ? "Import Complete"
                  : "Importing Roster…"}
          </Heading>

          <Text color={subtitleColor} fontSize="sm" mb={1}>
            {statusLabel}
          </Text>

          {!isDone && !error && (
            <Text color={subtitleColor} fontSize="xs" mb={6}>
              Please don't close this tab until the process is complete.
            </Text>
          )}

          {error && (
            <Alert
              status="error"
              borderRadius="lg"
              mt={3}
              mb={2}
              textAlign="left"
            >
              <AlertIcon />
              {error}
            </Alert>
          )}

          {hasErrorRows && !error && (
            <Alert
              status="warning"
              borderRadius="lg"
              mt={3}
              mb={2}
              textAlign="left"
            >
              <AlertIcon />
              <Box>
                <Text fontWeight="600" fontSize="sm">
                  {errorRowCount} of {totalRowCount}{" "}
                  {totalRowCount === 1 ? "row" : "rows"} could not be imported.
                </Text>
                <Text fontSize="sm" mt={1}>
                  You can continue to the next step or go back to review your
                  file and re-import.
                </Text>
              </Box>
            </Alert>
          )}

          {!error && (
            <>
              <Box position="relative" mb={2} mt={4}>
                <Progress
                  value={progress}
                  size="lg"
                  colorScheme={progressColor}
                  borderRadius="full"
                  hasStripe={!isDone}
                  isAnimated={!isDone}
                  bg="gray.100"
                />
              </Box>
              <Flex justify="space-between" align="center" px={1}>
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  color="gray.400"
                  textTransform="uppercase"
                >
                  Overall Progress
                </Text>
                <Text
                  fontWeight="800"
                  color={`${progressColor}.600`}
                  fontSize="md"
                >
                  {Math.round(progress)}%
                </Text>
              </Flex>
            </>
          )}
        </Box>

        {!error && (
          <Box
            w="full"
            p={4}
            bg={stageBg}
            borderRadius="xl"
            border="1px solid"
            borderColor={stageBorderColor}
          >
            <VStack align="stretch" spacing={3}>
              {STAGES.map((stage, i) => {
                const nextMin = STAGES[i + 1]?.min ?? 100;
                const isCompleted = isDone || progress >= nextMin;
                const isActive = !isDone && !error && currentStage === i;

                return (
                  <HStack key={stage.label} justify="space-between">
                    <HStack spacing={3}>
                      <Circle
                        size="8px"
                        bg={
                          isCompleted
                            ? "green.400"
                            : isActive
                              ? "blue.400"
                              : "gray.300"
                        }
                      />
                      <Text
                        fontSize="sm"
                        fontWeight={isActive ? "600" : "400"}
                        color={
                          isCompleted
                            ? "gray.400"
                            : isActive
                              ? headingColor
                              : "gray.400"
                        }
                        textDecoration={isCompleted ? "line-through" : "none"}
                      >
                        {stage.label}
                      </Text>
                    </HStack>

                    {isCompleted && (
                      <Badge
                        colorScheme="green"
                        variant="subtle"
                        borderRadius="full"
                        px={2}
                        fontSize="10px"
                      >
                        DONE
                      </Badge>
                    )}
                    {isActive && (
                      <Text fontSize="10px" fontWeight="bold" color="blue.500">
                        IN PROGRESS
                      </Text>
                    )}
                  </HStack>
                );
              })}
            </VStack>
          </Box>
        )}

        {isDone && !hasErrorRows && !error && (
          <Badge
            colorScheme="green"
            variant="subtle"
            px={4}
            py={2}
            borderRadius="full"
            fontSize="sm"
          >
            Roster imported successfully
          </Badge>
        )}

        {error && (
          <Button
            leftIcon={<ArrowBackIcon />}
            variant="outline"
            colorScheme="gray"
            onClick={handleBack}
            size="md"
          >
            Go Back
          </Button>
        )}

        {hasErrorRows && !error && (
          <HStack spacing={3} w="full" justify="center">
            <Button
              leftIcon={<ArrowBackIcon />}
              variant="outline"
              colorScheme="gray"
              onClick={handleBack}
              size="md"
            >
              Go Back
            </Button>
            <Button
              rightIcon={<ArrowForwardIcon />}
              colorScheme="orange"
              onClick={handleContinue}
              size="md"
            >
              Continue Anyway
            </Button>
          </HStack>
        )}
      </VStack>
    </ScaleFade>
  );
}
